import React, { useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { Button } from "../ui/button";
import { Icon } from "@iconify/react";
import Common from "../../helper/common";
import Utility from "../../helper/utilityHelper";

const PrintReceipt = forwardRef(({
  data = {},
  title = "PlayPro",
  subtitle = "",
  showPrintButton = true,
  showViewButton = false,
  buttonText = "Print",
  buttonIcon = "heroicons:printer",
  buttonSize = "sm",
  buttonVariant = "outline",
  onBeforePrint = null,
  onAfterPrint = null,
  autoPrint = false,
}, ref) => {
  const printRef = useRef();

  // Auto-print functionality
  useEffect(() => {
    if (autoPrint && Object.keys(data).length > 0) {
      const timer = setTimeout(() => {
        handlePrint();
      }, 1000); // Delay to ensure component is fully rendered

      return () => clearTimeout(timer);
    }
  }, [autoPrint, data]);

  // Expose print function to parent component
  useImperativeHandle(ref, () => ({
    print: handlePrint,
    view: handleView
  }));

  const handleView = () => {
    const printContent = printRef.current;

    if (!printContent) {
      console.error("Print content not available");
      return;
    }

    const winView = window.open("", "_blank", "width=580,height=800,scrollbars=yes,resizable=yes");

    if (!winView) {
      // Popup was blocked or failed to open
      console.error("Failed to open view window. Please check if popup blocker is enabled.");
      alert("View popup was blocked. Please allow popups for this site or try again.");
      return;
    }

    winView.document.write(`
      <html>
        <head>
          <title>PlayPro - Receipt Preview</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.2;
              color: #000;
              background: #f5f5f5;
              padding: 12px;
              margin: 0;
            }
            
            .print-container {
              max-width: 360px;
              margin: 0 auto;
              background: white;
              border: 2px solid #000;
              padding: 0;
              font-size: 11px;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }
            
            .print-header {
              background: white;
              color: #000;
              padding: 10px 15px;
              text-align: center;
              border-bottom: 1px solid #000;
            }
            
            .print-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 3px;
              text-transform: uppercase;
              color: #000;
            }
            
            .print-subtitle {
              font-size: 12px;
              margin: 0;
              font-weight: normal;
              color: #000;
              text-align: center;
            }

            .print-logo {
              margin-bottom: 0;
            }

            .club-logo {
              width: 100px;
              height: 60px;
              object-fit: contain;
              border-radius: 0px;
            }

            .print-logo-title-container {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              margin-bottom: 5px;
            }

            .print-title-section {
              /* flex: 1; */
            }

            .print-title {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 2px;
              text-transform: uppercase;
              color: #000;
              text-align: center;
            }

            .print-phone {
              font-size: 10px;
              margin: 0;
              font-weight: normal;
              color: #000;
              text-align: center;
            }
            
            .print-content {
              padding: 10px 15px;
              background: white;
            }
            
            .print-item {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding: 2px 0;
              margin-bottom: 1px;
              border-bottom: none;
            }
            
            .print-item:last-child {
              border-bottom: none;
              margin-bottom: 0;
            }
            
            .print-key {
              font-weight: normal;
              color: #000;
              flex: 1;
              margin-right: 10px;
              font-size: 11px;
              text-align: left;
            }
            
            .print-value {
              color: #000;
              text-align: right;
              font-weight: bold;
              font-size: 11px;
              min-width: 70px;
            }
            
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin: 8px 0;
            }
            
            .items-table th {
              background: #f5f5f5;
              border: 1px solid #000;
              padding: 4px 6px;
              font-size: 10px;
              font-weight: bold;
              text-align: left;
            }
            
            .items-table td {
              border: 1px solid #000;
              padding: 4px 6px;
              font-size: 10px;
            }
            
            .items-table .item-name {
              text-align: left;
              font-weight: bold;
            }
            
            .items-table .item-qty {
              text-align: center;
              width: 30px;
            }
            
            .items-table .item-price {
              text-align: right;
              width: 60px;
            }
            
            .items-table .item-total {
              text-align: right;
              width: 60px;
              font-weight: bold;
            }
            
            .print-footer {
              background: white;
              padding: 8px 15px;
              text-align: center;
              border-top: 1px solid #000;
              color: #000;
              font-size: 10px;
            }
            
            .print-datetime {
              font-weight: normal;
              color: #000;
              margin-top: 4px;
              font-size: 9px;
            }
            
            .print-company {
              font-weight: normal;
              color: #000;
              margin-bottom: 2px;
              font-size: 10px;
            }
            
            .print-email {
              font-weight: normal;
              color: #000;
              margin-bottom: 3px;
              font-size: 10px;
            }
            
            .divider {
              height: 1px;
              background: #000;
              margin: 6px 0 4px 0;
            }
            
            .empty-state {
              text-align: center;
              padding: 25px 15px;
              color: #000;
            }
            
            .empty-icon {
              font-size: 1.5rem;
              margin-bottom: 8px;
              opacity: 0.5;
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    try {
      winView.document.close();
      winView.focus();
    } catch (error) {
      console.error("Failed to write to view window:", error);
      if (winView) {
        winView.close();
      }
    }
  };

  const handlePrint = () => {
    if (onBeforePrint) onBeforePrint();

    const printContent = printRef.current;

    if (!printContent) {
      console.error("Print content not available");
      return;
    }

    const winPrint = window.open("", "_blank", "width=580,height=800,scrollbars=yes,resizable=yes");

    if (!winPrint) {
      // Popup was blocked or failed to open
      console.error("Failed to open print window. Please check if popup blocker is enabled.");
      // alert("Print popup was blocked. Please allow popups for this site or try again.");
      return;
    }

    winPrint.document.write(`
      <html>
        <head>
          <title>PlayPro - Receipt</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.2;
              color: #000;
              background: white;
              padding: 6px;
              margin: 0;
            }
            
            .print-container {
              max-width: 360px;
              margin: 0 auto;
              background: white;
              border: 2px solid #000;
              padding: 0;
              font-size: 11px;
            }
            
            .print-header {
              background: white;
              color: #000;
              padding: 10px 15px;
              text-align: center;
              border-bottom: 1px solid #000;
            }
            
            .print-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 3px;
              text-transform: uppercase;
              color: #000;
            }
            
            .print-subtitle {
              font-size: 12px;
              margin: 0;
              font-weight: normal;
              color: #000;
            }

            .print-logo {
              margin-bottom: 0;
            }

            .club-logo {
              width: 100px;
              height: 60px;
              object-fit: contain;
              border-radius: 4px;
            }

            .print-logo-title-container {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              margin-bottom: 5px;
            }

            .print-title-section {
              /* flex: 1; */
            }

            .print-title {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 2px;
              text-transform: uppercase;
              color: #000;
              text-align: center;
            }

            .print-phone {
              font-size: 10px;
              margin: 0;
              font-weight: normal;
              color: #000;
              text-align: center;
            }
            
            .print-content {
              padding: 10px 15px;
              background: white;
            }
            
            .print-item {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding: 2px 0;
              margin-bottom: 1px;
              border-bottom: none;
            }
            
            .print-item:last-child {
              border-bottom: none;
              margin-bottom: 0;
            }
            
            .print-key {
              font-weight: normal;
              color: #000;
              flex: 1;
              margin-right: 10px;
              font-size: 11px;
              text-align: left;
            }
            
            .print-value {
              color: #000;
              text-align: right;
              font-weight: bold;
              font-size: 11px;
              min-width: 70px;
            }
            
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin: 8px 0;
            }
            
            .items-table th {
              background: #f5f5f5;
              border: 1px solid #000;
              padding: 4px 6px;
              font-size: 10px;
              font-weight: bold;
              text-align: left;
            }
            
            .items-table td {
              border: 1px solid #000;
              padding: 4px 6px;
              font-size: 10px;
            }
            
            .items-table .item-name {
              text-align: left;
              font-weight: bold;
            }
            
            .items-table .item-qty {
              text-align: center;
              width: 30px;
            }
            
            .items-table .item-price {
              text-align: right;
              width: 60px;
            }
            
            .items-table .item-total {
              text-align: right;
              width: 60px;
              font-weight: bold;
            }
            
            .print-footer {
              background: white;
              padding: 8px 15px;
              text-align: center;
              border-top: 1px solid #000;
              color: #000;
              font-size: 10px;
            }
            
            .print-datetime {
              font-weight: normal;
              color: #000;
              margin-top: 4px;
              font-size: 9px;
            }
            
            .print-company {
              font-weight: normal;
              color: #000;
              margin-bottom: 2px;
              font-size: 10px;
            }
            
            .print-email {
              font-weight: normal;
              color: #000;
              margin-bottom: 3px;
              font-size: 10px;
            }
            
            .divider {
              height: 1px;
              background: #000;
              margin: 6px 0 4px 0;
            }
            
            @media print {
              body {
                padding: 0;
                margin: 0;
                background: white !important;
              }
              
              .print-container {
                border: 2px solid #000 !important;
                box-shadow: none;
                margin: 0;
                max-width: none;
                background: white !important;
              }
              
              .print-header {
                padding: 5px 5px;
                background: white !important;
                color: #000 !important;
                border-bottom: 1px solid #000 !important;
                text-align: center !important;
              }
              
              .print-title {
                color: #000 !important;
              }
              
              .print-subtitle {
                color: #000 !important;
              }

              .print-logo {
                margin-bottom: 0 !important;
              }

              .club-logo {
                width: 100px !important;
                height: 60px !important;
                object-fit: contain !important;
                border-radius: 0px !important;
              }

              .print-logo-title-container {
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                gap: 10px !important;
                margin-bottom: 5px !important;
              }

              .print-title-section {
                /* flex: 1 !important; */
              }

              .print-title {
                font-size: 14px !important;
                font-weight: bold !important;
                margin-bottom: 2px !important;
                text-transform: uppercase !important;
                color: #000 !important;
                text-align: center !important;
              }

              .print-phone {
                font-size: 10px !important;
                margin: 0 !important;
                font-weight: normal !important;
                color: #000 !important;
                text-align: center !important;
              }
              
              .print-content {
                padding: 10px 15px;
                background: white !important;
              }
              
              .print-footer {
                padding: 8px 15px;
                background: white !important;
                border-top: 1px solid #000 !important;
              }
              
              .print-item {
                padding: 2px 0;
                margin-bottom: 1px;
                page-break-inside: avoid;
                border-bottom: none !important;
              }
              
              .print-item:last-child {
                margin-bottom: 0;
              }
              
              .print-key, .print-value {
                color: #000 !important;
              }
              
              .print-value {
                font-weight: bold !important;
              }
              
              .print-datetime, .print-company, .print-email {
                color: #000 !important;
              }
              
              .divider {
                background: #000 !important;
              }
              
              .items-table {
                width: 100% !important;
                border-collapse: collapse !important;
                margin: 8px 0 !important;
              }
              
              .items-table th {
                background: #f5f5f5 !important;
                border: 1px solid #000 !important;
                padding: 4px 6px !important;
                font-size: 10px !important;
                font-weight: bold !important;
                text-align: left !important;
              }
              
              .items-table td {
                border: 1px solid #000 !important;
                padding: 4px 6px !important;
                font-size: 10px !important;
              }
              
              .items-table .item-name {
                text-align: left !important;
                font-weight: bold !important;
              }
              
              .items-table .item-qty {
                text-align: center !important;
                width: 30px !important;
              }
              
              .items-table .item-price {
                text-align: right !important;
                width: 60px !important;
              }
              
              .items-table .item-total {
                text-align: right !important;
                width: 60px !important;
                font-weight: bold !important;
              }
            }
            
            .empty-state {
              text-align: center;
              padding: 25px 15px;
              color: #000;
            }
            
            .empty-icon {
              font-size: 1.5rem;
              margin-bottom: 8px;
              opacity: 0.5;
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    try {
      winPrint.document.close();
      winPrint.focus();

      // Add event listener for after print
      winPrint.onafterprint = () => {
        if (onAfterPrint) onAfterPrint();
        winPrint.close();
      };

      setTimeout(() => {
        try {
          winPrint.print();
        } catch (error) {
          console.error("Print error:", error);
          winPrint.close();
        }
      }, 500);
    } catch (error) {
      console.error("Failed to write to print window:", error);
      if (winPrint) {
        winPrint.close();
      }
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object" && value instanceof Date) {
      return Common.moment(value).format("DD/MM/YYYY hh:mm A");
    }
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value);
  };

  const formatKey = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const renderItemsTable = (data) => {
    // Check if there are any items to display
    const itemEntries = Object.entries(data).filter(([key, value]) => {
      // Look for entries that match item pattern
      return key.includes('productName') || key.includes('Product #') || (typeof value === 'string' && value.includes(' x PKR ') && value.includes(' = PKR '));
    });

    if (itemEntries.length === 0) return null;

    return (
      <div>
        <div className="print-item">
          <div className="print-key" style={{ fontWeight: 'bold', textAlign: 'center', borderBottom: '1px solid #000', paddingBottom: '4px', marginBottom: '8px' }}>ORDER ITEMS</div>
        </div>
        <table className="items-table">
          <thead>
            <tr>
              <th className="item-name">Item</th>
              <th className="item-qty">Qty</th>
              <th className="item-price">Price</th>
              <th className="item-total">Total</th>
            </tr>
          </thead>
          <tbody>
            {itemEntries.map(([itemName, itemDetails], index) => {
              // Parse item details like "3 x PKR 480 = PKR 1440"
              const match = itemDetails.match(/(\d+)\s*x\s*PKR\s*(\d+)\s*=\s*PKR\s*(\d+)/);
              if (match) {
                const [, qty, price, total] = match;
                return (
                  <tr key={index}>
                    <td className="item-name">{itemName.replace('Product #', 'Item #')}</td>
                    <td className="item-qty">{qty}</td>
                    <td className="item-price">PKR {price}</td>
                    <td className="item-total">PKR {total}</td>
                  </tr>
                );
              }
              return null;
            }).filter(Boolean)}
          </tbody>
        </table>
      </div>
    );
  };

  // Extract special fields from data
  const { clubPhone, clubLogo, ...printData } = data;

  return (
    <>
      <div className="flex items-center gap-2">
        {showViewButton && (
          <Button
            variant={buttonVariant}
            size={buttonSize}
            onClick={handleView}
            className="flex items-center gap-1"
          >
            <Icon icon="heroicons:eye" width={14} />
            <span className="text-xs">View</span>
          </Button>
        )}
        {showPrintButton && (
          <Button
            variant={buttonVariant}
            size={buttonSize}
            onClick={handlePrint}
            className="flex items-center gap-1"
          >
            <Icon icon={buttonIcon} width={14} />
            <span className="text-xs">{buttonText || "Print"}</span>
          </Button>
        )}
      </div>

      <div ref={printRef} style={{ display: "none" }}>
        <div className="print-container">
          <div className="print-header">
            <div className="print-logo-title-container">
              {/* {clubLogo && (
                <div className="print-logo">
                  <img
                    src={Utility.getImagePath(clubLogo)}
                    alt="Club Logo"
                    className="club-logo"
                  />
                </div>
              )} */}
              <div className="print-title-section">
                {clubLogo && (
                  <img
                    src={Utility.getImagePath(clubLogo)}
                    alt="Club Logo"
                    className="club-logo"
                  />
                )}
                <h5 className="print-title">{title}</h5>
                {clubPhone && (
                  <p className="print-phone">{clubPhone}</p>
                )}
              </div>
            </div>
            {subtitle && <p className="print-subtitle">{subtitle}</p>}
          </div>

          <div className="print-content">
            {Object.keys(printData).length > 0 ? (
              <>
                {/* Render non-item data first */}
                {Object.entries(printData).map(([key, value], index) => {
                  // Skip items and separators for main info
                  if (key.includes('productName') || key.includes('Product #') ||
                    (typeof value === 'string' && value.includes(' x PKR ') && value.includes(' = PKR ')) ||
                    key === 'ITEMS' || key === '---' || key === '----') {
                    return null;
                  }

                  return (
                    <div key={index} className="print-item">
                      <div className="print-key">{formatKey(key)}</div>
                      <div className="print-value">{formatValue(value)}</div>
                    </div>
                  );
                })}

                {/* Render items table */}
                {renderItemsTable(printData)}

                <div className="divider"></div>
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">📄</div>
                <p>No data available to print</p>
              </div>
            )}
          </div>

          <div className="print-footer">
            <p className="print-company">
              Powered by PlayPro
            </p>
            {/* <p className="print-email">
              info@playpro.pk
            </p> */}
            <p className="print-datetime">
              {/* Printed on: {Common.moment().add(5, 'hours').format("DD/MM/YYYY hh:mm A")} */}
              Printed on: {Common.moment().utcOffset(300).format("DD/MM/YYYY hh:mm A")}

            </p>
          </div>
        </div>
      </div>
    </>
  );
});

export default PrintReceipt;